import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { axios } from 'ApiClient';
import PropTypes from 'prop-types';
import _ from 'lodash';
import CurrencyField from '../../../../../shared/components/form/CurrencyFieldF';
import { dealCurrencyCode, getCurrencySymbol, formatCurrency } from '../../../../../shared/helpers/WVFormatter';

const MaxAmount = React.memo(({
  record, updateRecordFunction, setCurrentProspect, setShowNotesModal,
}) => {
  const cpmFeePercentage = useSelector(state => state.session?.advertiser?.cpmFeePercentage || 0);
  const contentLicenseFeePercentage = useSelector(state => state.session?.advertiser?.contentLicenseFeePercentage || 0);
  const isContentLicense = record.params?.dealObject?.dealTerms?.type_of_deal === 'Content License';
  const feePercentage = isContentLicense ? contentLicenseFeePercentage : cpmFeePercentage;
  const maxPrice = record.params?.dealObject?.dealTerms.max_price || 0;
  const totalAmount = maxPrice + (maxPrice * feePercentage);
  const [amount, setAmount] = useState(totalAmount);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const mp = record.params?.dealObject?.dealTerms.max_price || 0;
    setAmount(mp + (mp * feePercentage));
  }, [record, feePercentage]);

  const toBaseMaxPrice = (total) => {
    if (feePercentage > 0) {
      return Number((total / (1 + feePercentage)).toFixed(2));
    }
    return total;
  };

  const validateAmount = () => {
    if (!(amount > 0)) {
      setError(`The Contract Amount must be greater than ${getCurrencySymbol(dealCurrencyCode(record.params.dealObject))}0`);
      const mp = record.params.dealObject.dealTerms.max_price || 0;
      setAmount(mp + (mp * feePercentage));
      return false;
    }
    return true;
  };

  const handleChange = async () => {
    if (!validateAmount()) return true;
    setLoading(true);
    setError(null);

    const baseAmount = toBaseMaxPrice(amount);
    const currentMaxPrice = record.params?.dealObject?.dealTerms.max_price || 0;

    // if (record.params?.dealObject?.dealTerms?.guaranteed_payment_contract_amount && baseAmount < record.params.dealObject.dealTerms.guaranteed_payment_contract_amount) {
    //   setError('Max Contract Amount cannot be less than Guaranteed Payment Amount');
    //   const mp = record.params.dealObject.dealTerms.max_price || 0;
    //   setAmount(mp + (mp * cpmFeePercentage));
    //   setLoading(false);
    //   return true;
    // }

    if ((!currentMaxPrice && baseAmount) || (currentMaxPrice && baseAmount !== currentMaxPrice)) {
      try {
        const data = await axios.post('/api/brand/prospects/post-update-cpm-prospect-property', {
          ids: [record.id],
          values: {
            // The amount as typed, with the fee; the server derives max_price from it
            brandApprovedAmount: Number(amount),
          },
        });
        if (data?.data?.recordPopulated && updateRecordFunction) {
          updateRecordFunction(data?.data?.recordPopulated);
        }
        setCurrentProspect(record);
        setShowNotesModal(true);
      } catch (e) {
        setError(e.message);
      }
    }

    setLoading(false);
    return true;
  };

  const status = record.params?.approvalsBrandStatus?.status;
  const isReadOnly = status === 'approved' || status === 'rejected' || !record.params.isBrandStatusChangeAllowed;
  const currency = dealCurrencyCode(record.params.dealObject);

  if (isReadOnly) {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span>{formatCurrency(Number(amount), currency, 2, true)}</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <div style={{ flex: 1 }}>
        <CurrencyField
          placeholder="Payout Amount"
          disabled={loading}
          value={amount}
          setValue={value => setAmount(value)}
          onBlur={handleChange}
          currency={currency}
        />
        {error ? (<span className="text-danger" style={{ fontSize: '11px' }}>{error}</span>) : null}
      </div>
    </div>
  );
}, (a, a1) => _.isEqual(a.record, a1.record));

MaxAmount.propTypes = {
  record: PropTypes.objectOf(PropTypes.any).isRequired,
  updateRecordFunction: PropTypes.func.isRequired,
  setCurrentProspect: PropTypes.func.isRequired,
  setShowNotesModal: PropTypes.func.isRequired,
};

export default MaxAmount;
